const API_URL = process.env.REACT_APP_BACKEND_URL;
const JSON_METHODS = new Set(["POST", "PUT", "PATCH"]);
const STATUS_MESSAGES = {
  403: "The server blocked this action before it could start. Please sign in again and retry.",
  502: "The preview service could not finish publishing. Please retry.",
  503: "The preview service is temporarily unavailable. Please try again shortly.",
  504: "The preview service took too long to respond. Please retry.",
};

function detailMessage(detail, status) {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map((item) => item?.msg || JSON.stringify(item)).join(" ");
  if (detail?.message) return detail.message;
  if (detail?.error?.message) return detail.error.message;
  return STATUS_MESSAGES[status] || "Something went wrong. Please try again.";
}

function csrfToken() {
  if (typeof document === "undefined") return "";
  return document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith("admin_csrf_token="))?.split("=").slice(1).join("=") || "";
}

async function request(path, options = {}) {
  const csrf = csrfToken();
  const method = String(options.method || "GET").toUpperCase();
  const requestBody = options.body ?? (JSON_METHODS.has(method) ? JSON.stringify({}) : undefined);
  let response;
  try {
    response = await fetch(`${API_URL}/api${path}`, {
      credentials: "include",
      ...options,
      method,
      ...(requestBody === undefined ? {} : { body: requestBody }),
      headers: { "Content-Type": "application/json", ...(csrf ? { "X-Admin-CSRF-Token": csrf } : {}), ...(options.headers || {}) },
    });
  } catch {
    const error = new Error("The Factory server could not be reached. Please try again.");
    error.status = 0;
    throw error;
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(detailMessage(body.detail, response.status));
    error.status = response.status;
    throw error;
  }
  return body;
}

async function multipartRequest(path, formData) {
  let response;
  try {
    response = await fetch(`${API_URL}/api${path}`, {
      method: "POST",
      credentials: "include",
      headers: csrfToken() ? { "X-Admin-CSRF-Token": csrfToken() } : {},
      body: formData,
    });
  } catch {
    throw new Error("The Factory server could not be reached. Please try again.");
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(detailMessage(body.detail, response.status));
    error.status = response.status;
    throw error;
  }
  return body;
}

async function downloadBlob(path) {
  const response = await fetch(`${API_URL}/api${path}`, { credentials: "include", headers: csrfToken() ? { "X-Admin-CSRF-Token": csrfToken() } : {} });
  if (!response.ok) throw new Error("The markup attachment could not be loaded.");
  return response.blob();
}

export function getAdminMe() {
  return request("/auth/me");
}

export function loginAdmin(email, password) {
  return request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
}

export function logoutAdmin() {
  return request("/auth/logout", { method: "POST" });
}

export function getAdminSummary() {
  return request("/admin/summary");
}

export function getAdminLeads(params = {}) {
  const query = new URLSearchParams();
  if (params.status && params.status !== "all") query.set("status", params.status);
  if (params.q) query.set("q", params.q);
  return request(`/admin/leads${query.toString() ? `?${query}` : ""}`);
}

export function updateAdminLeadStatus(leadId, status, note = "") {
  return request(`/admin/leads/${leadId}/status`, { method: "PATCH", body: JSON.stringify({ status, note }) });
}

export function addAdminLeadNote(leadId, text) {
  return request(`/admin/leads/${leadId}/notes`, { method: "POST", body: JSON.stringify({ text }) });
}

export function getAdminSites() {
  return request("/admin/sites");
}

export function createSourceIntake(sourceUrl, customizationBrief = {}, startImmediately = true) {
  return request("/admin/intake", { method: "POST", body: JSON.stringify({ sourceUrl, customizationBrief, startImmediately }) });
}

export function startSourceIntake(siteId) {
  return request(`/admin/intake/${siteId}/start`, { method: "POST" });
}

export function getSourceIntake(siteId) {
  return request(`/admin/intake/${siteId}`);
}

export function retrySourceIntake(siteId) {
  return request(`/admin/intake/${siteId}/retry`, { method: "POST" });
}

export function getSourceIntakeAssets(siteId) {
  return request(`/admin/intake/${siteId}/assets`);
}

async function fileSha256(file) {
  if (typeof crypto === "undefined" || !crypto.subtle) return "";
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function uploadSourceIntakeAsset(siteId, file, metadata, onProgress) {
  const chunkSize = 1_500_000;
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
  const initiated = await request(`/admin/intake/${siteId}/assets/initiate`, { method: "POST", body: JSON.stringify({ ...metadata, filename: file.name, contentType: file.type || metadata.contentType, sizeBytes: file.size, totalChunks }) });
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
    const formData = new FormData();
    formData.append("file", file.slice(chunkIndex * chunkSize, Math.min(file.size, (chunkIndex + 1) * chunkSize)), file.name);
    formData.append("uploadId", initiated.uploadId);
    formData.append("chunkIndex", String(chunkIndex));
    await multipartRequest(`/admin/intake/${siteId}/assets/${initiated.uploadId}/chunks`, formData);
    onProgress?.(Math.round(((chunkIndex + 1) / totalChunks) * 90));
  }
  const sha256 = await fileSha256(file);
  const asset = await request(`/admin/intake/${siteId}/assets/complete`, { method: "POST", body: JSON.stringify({ uploadId: initiated.uploadId, ...(sha256 ? { sha256 } : {}) }) });
  onProgress?.(100);
  return asset;
}

export function getMarkupDrafts() {
  return request("/admin/markup/drafts");
}

export function createMarkupDraft(title = "Untitled template draft") {
  return request("/admin/markup/drafts", { method: "POST", body: JSON.stringify({ title }) });
}

export function updateMarkupDraft(draftId, updates) {
  return request(`/admin/markup/drafts/${draftId}`, { method: "PATCH", body: JSON.stringify(updates) });
}

export function addMarkupMessage(draftId, payload) {
  return request(`/admin/markup/drafts/${draftId}/messages`, { method: "POST", body: JSON.stringify(payload) });
}

export function undoMarkupDraft(draftId) {
  return request(`/admin/markup/drafts/${draftId}/undo`, { method: "POST" });
}

export function restoreMarkupDraft(draftId, historyIndex) {
  return request(`/admin/markup/drafts/${draftId}/restore`, { method: "POST", body: JSON.stringify({ historyIndex }) });
}

export function publishMarkupDraft(draftId) {
  return request(`/admin/markup/drafts/${draftId}/publish`, { method: "POST" });
}

export async function uploadMarkupImage(draftId, file, role, onProgress) {
  const uploadId = typeof crypto?.randomUUID === "function" ? crypto.randomUUID() : `upload_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const chunkSize = 1024 * 1024;
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
    const formData = new FormData();
    formData.append("file", file.slice(chunkIndex * chunkSize, Math.min(file.size, (chunkIndex + 1) * chunkSize)), file.name);
    formData.append("upload_id", uploadId);
    formData.append("chunk_index", String(chunkIndex));
    formData.append("total_chunks", String(totalChunks));
    formData.append("role", role);
    formData.append("original_filename", file.name);
    formData.append("content_type", file.type || "application/octet-stream");
    await multipartRequest(`/admin/markup/drafts/${draftId}/attachments/chunks`, formData);
    onProgress?.(Math.round(((chunkIndex + 1) / totalChunks) * 90));
  }
  const finalize = new FormData();
  finalize.append("upload_id", uploadId);
  const attachment = await multipartRequest(`/admin/markup/drafts/${draftId}/attachments/finalize`, finalize);
  onProgress?.(100);
  return attachment;
}

export async function getMarkupAttachmentContent(attachmentId) {
  return downloadBlob(`/admin/markup/attachments/${attachmentId}/content`);
}

export function getPublicTemplateMarkup() {
  return request("/public/template-markup");
}

export function getAdminSessions() {
  return request("/admin/chat/sessions");
}

export function createAdminSession(siteId = null) {
  return request("/admin/chat/sessions", { method: "POST", body: JSON.stringify(siteId ? { siteId } : {}) });
}

export function getAdminSession(sessionId) {
  return request(`/admin/chat/sessions/${sessionId}`);
}

export function getAdminProposals() {
  return request("/admin/proposals");
}

export function setProposalScope(proposalId, mode, siteIds) {
  return request(`/admin/proposals/${proposalId}/scope`, { method: "POST", body: JSON.stringify({ mode, siteIds }) });
}

export function createProposalPreview(proposalId) {
  return request(`/admin/proposals/${proposalId}/preview`, { method: "POST" });
}

export function approveProposal(proposalId) {
  return request(`/admin/proposals/${proposalId}/approve`, { method: "POST" });
}

export async function streamAdminMessage(sessionId, text, onEvent) {
  const response = await fetch(`${API_URL}/api/admin/chat/sessions/${sessionId}/messages`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(csrfToken() ? { "X-Admin-CSRF-Token": csrfToken() } : {}), Accept: "text/event-stream" },
    body: JSON.stringify({ text }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(detailMessage(body.detail));
  }
  if (!response.body) throw new Error("The assistant stream is unavailable.");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const consume = (chunk) => {
    buffer += chunk;
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() || "";
    blocks.forEach((block) => {
      const line = block.split("\n").find((value) => value.startsWith("data: "));
      if (!line) return;
      try { onEvent(JSON.parse(line.slice(6))); } catch { /* Ignore an incomplete event. */ }
    });
  };
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    consume(decoder.decode(value, { stream: true }));
  }
  consume(decoder.decode());
}
